import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LoaderCircle, Search } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useState } from "react";
import { TextButton } from "../actions/text-button";
import { type CommandItem, CommandPalette } from "../components/arc/command-palette/command-palette";
import { SearchField } from "../components/arc/search-field/search-field";
import styles from "./search.module.css";

export interface SearchResult {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  /** Matched too, besides the label and description (static groups only). */
  keywords?: string[];
}

export interface SearchGroup {
  id: string;
  label: string;
  results: SearchResult[];
  /** Results still on their way: the group shows a "Searching" row meanwhile. */
  loading?: boolean;
  /** The group couldn't be searched: shown in place of its results. */
  error?: string;
  /**
   * The palette filters these itself as you type (actions, pages). Other groups come from a search
   * for the typed text and are shown as they are.
   */
  local?: boolean;
}

export interface SearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What's typed, as the palette reports it. */
  onQueryChange: (query: string) => void;
  /** The text the record groups were searched for; empty when nothing is typed. */
  query: string;
  groups: SearchGroup[];
  onSelect: (groupId: string, result: SearchResult) => void;
  placeholder?: string;
  /** Under the palette: what can be found, and the keys. */
  hint?: string;
  /** Shown as a row when the record groups have all answered and found nothing for the text. */
  noResults?: { label: (query: string) => string; description?: string };
}

const SEP = "\u0000";

/**
 * Search from anywhere (⌘K, P8-58): Arc's CommandPalette in a dialog. Actions and pages are
 * filtered by the palette; records come from the API for the typed text and are passed back in as
 * items (each carries the text as a keyword, so the palette keeps them). The palette owns its
 * input, so the typed text is read from its change events as they bubble.
 */
export function SearchPalette({
  open,
  onOpenChange,
  onQueryChange,
  query,
  groups,
  onSelect,
  placeholder = "Search",
  hint,
  noResults,
}: SearchPaletteProps) {
  const typed = query.trim();
  const items: CommandItem[] = groups.flatMap((g) => {
    const keep = g.local ? undefined : [typed];
    const rows: CommandItem[] = g.results.map((r) => ({
      id: `${g.id}${SEP}${r.id}`,
      label: r.label,
      description: r.description,
      icon: r.icon,
      group: g.label,
      keywords: g.local ? r.keywords : keep,
    }));
    if (!g.local && typed && g.loading && rows.length === 0)
      rows.push({
        id: `${g.id}${SEP}${SEP}loading`,
        label: `Searching ${g.label.toLowerCase()}…`,
        group: g.label,
        keywords: keep,
        icon: <LoaderCircle size={16} className={styles.spin} />,
      });
    if (!g.local && typed && g.error)
      rows.push({ id: `${g.id}${SEP}${SEP}error`, label: g.error, group: g.label, keywords: keep });
    return rows;
  });
  const records = groups.filter((g) => !g.local);
  const settled = records.length > 0 && records.every((g) => !g.loading && !g.error && g.results.length === 0);
  if (typed && noResults && settled)
    items.push({
      id: `${SEP}${SEP}none`,
      label: noResults.label(typed),
      description: noResults.description,
      group: "Results",
      keywords: [typed],
      icon: <Search size={16} />,
    });
  const pick = (item: CommandItem) => {
    const [groupId, resultId] = item.id.split(SEP);
    if (!resultId) return; // a "Searching" or error row
    const result = groups.find((g) => g.id === groupId)?.results.find((r) => r.id === resultId);
    if (result) onSelect(groupId, result);
  };
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onQueryChange("");
        onOpenChange(next);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={styles.overlay} />
        <DialogPrimitive.Content
          className={styles.paletteHost}
          aria-describedby={undefined}
          // Escape first clears the text, then closes: the palette decides, not the dialog
          onEscapeKeyDown={(event) => event.preventDefault()}
          onChange={(event) => {
            const target = event.target as HTMLInputElement;
            if (target.getAttribute("role") === "combobox") onQueryChange(target.value);
          }}
          onKeyDown={(event) => {
            const target = event.target as HTMLElement;
            if (event.key === "Escape" && target.getAttribute("role") === "combobox") onQueryChange("");
          }}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('[aria-label="Clear search"]')) onQueryChange("");
          }}
        >
          <DialogPrimitive.Title className={styles.visuallyHidden}>Search</DialogPrimitive.Title>
          <CommandPalette
            items={items}
            placeholder={placeholder}
            label={placeholder}
            autoFocus
            onSelect={pick}
            onClose={() => {
              onQueryChange("");
              onOpenChange(false);
            }}
          />
          {hint ? <p className={styles.paletteHint}>{hint}</p> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** The top bar's way into search: a field on desktop with its shortcut, an icon on phones. */
export function SearchTrigger({ onOpen, placeholder }: { onOpen: () => void; placeholder: string }) {
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <>
      <button type="button" className={styles.trigger} onClick={onOpen} aria-keyshortcuts="Control+K Meta+K">
        <Search size={16} aria-hidden="true" />
        <span className={styles.placeholder}>{placeholder}</span>
        <kbd className={styles.kbd}>{mac ? "⌘K" : "Ctrl K"}</kbd>
      </button>
      <button type="button" className={styles.iconTrigger} onClick={onOpen} aria-label={placeholder}>
        <Search size={18} aria-hidden="true" />
      </button>
    </>
  );
}

/** Opens search on ⌘K / Ctrl+K from anywhere on the page. */
export function useSearchShortcut(open: () => void) {
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        open();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
}

export interface SearchSelectProps {
  label: string;
  /** The chosen record, shown as a card with Change; null to search. */
  value: SearchResult | null;
  onChange: (value: SearchResult | null) => void;
  query: string;
  onQueryChange: (query: string) => void;
  results: SearchResult[];
  /** Typing hasn't settled or results are loading. */
  pending?: boolean;
  placeholder?: string;
  description?: string;
  error?: string;
}

/**
 * Pick one record by searching the API, e.g. a customer for a new order. Arc's Combobox filters a
 * fixed list in the browser; this asks the server as you type, with the palette's keyboard:
 * arrows, Home and End, Enter.
 */
export function SearchSelect({
  label,
  value,
  onChange,
  query,
  onQueryChange,
  results,
  pending = false,
  placeholder,
  description,
  error,
}: SearchSelectProps) {
  const listId = useId();
  const [active, setActive] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new query starts at the top
  useEffect(() => setActive(0), [query]);
  const optionId = (i: number) => `${listId}-option-${i}`;

  if (value) {
    return (
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <div className={styles.chosen}>
          <span className={styles.text}>
            <span className={styles.label}>{value.label}</span>
            {value.description ? <span className={styles.description}>{value.description}</span> : null}
          </span>
          <TextButton onClick={() => onChange(null)}>Change</TextButton>
        </div>
      </div>
    );
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    const move = { ArrowDown: active + 1, ArrowUp: active - 1, Home: 0, End: results.length - 1 }[event.key];
    if (move !== undefined) {
      event.preventDefault();
      setActive((move + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const chosen = results[active];
      if (chosen) onChange(chosen);
    }
  };
  const searching = query.trim().length > 0;

  return (
    <div className={styles.field}>
      <SearchField
        label={label}
        value={query}
        onValueChange={onQueryChange}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={results.length > 0 ? optionId(active) : undefined}
        aria-invalid={error ? true : undefined}
        onKeyDown={onKeyDown}
      />
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : description && !searching ? (
        <p className={styles.hint}>{description}</p>
      ) : null}
      {searching && !pending && results.length === 0 ? (
        <p className={styles.hint} role="status">
          Nothing found for “{query.trim()}”.
        </p>
      ) : null}
      {results.length > 0 ? (
        <div id={listId} role="listbox" aria-label={label} className={styles.inlineResults}>
          {results.map((result, i) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the field owns the keyboard (aria-activedescendant)
            <div
              key={result.id}
              id={optionId(i)}
              role="option"
              tabIndex={-1}
              aria-selected={i === active}
              className={styles.option}
              onMouseMove={() => setActive(i)}
              onClick={() => onChange(result)}
            >
              <span className={styles.text}>
                <span className={styles.label}>{result.label}</span>
                {result.description ? <span className={styles.description}>{result.description}</span> : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
